import { memo, useState, useCallback, useRef, useEffect } from 'react';
import { cn } from '../../lib/styles';

export interface HoverActionsProps {
  /** The content that will trigger the hover actions */
  children: React.ReactNode;
  /** The actions to display on hover */
  actions: React.ReactNode;
  alwaysDisplayActions?: boolean;
  /** Delay in milliseconds before showing the actions */
  showDelay?: number;
  /** Position of the actions relative to the container */
  actionsPosition?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  className?: string;
  actionsClassName?: string;
  stopClickPropagation?: boolean;
}

const positionClasses = {
  'top-right': 'right-0 -top-6',
  'top-left': 'left-0 -top-6',
  'bottom-right': 'right-0 -bottom-6',
  'bottom-left': 'left-0 -bottom-6',
};

const HoverActionsBase = ({
  children,
  actions,
  alwaysDisplayActions = false,
  showDelay = 0,
  actionsPosition = 'top-right',
  className,
  actionsClassName,
  stopClickPropagation = true,
}: HoverActionsProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showActions, setShowActions] = useState(alwaysDisplayActions);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDelayTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    if (showDelay > 0) {
      clearDelayTimeout();
      timeoutRef.current = setTimeout(() => {
        setShowActions(true);
      }, showDelay);
    } else {
      setShowActions(true);
    }
  }, [showDelay, clearDelayTimeout]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    clearDelayTimeout();
    if (!alwaysDisplayActions) {
      setShowActions(false);
    }
  }, [alwaysDisplayActions, clearDelayTimeout]);

  const handleActionsClick = useCallback(
    (event: React.MouseEvent) => {
      if (stopClickPropagation) {
        event.stopPropagation();
      }
    },
    [stopClickPropagation],
  );

  useEffect(() => {
    return () => {
      clearDelayTimeout();
    };
  }, [clearDelayTimeout]);

  useEffect(() => {
    if (alwaysDisplayActions) {
      setShowActions(true);
    }
  }, [alwaysDisplayActions]);

  const shouldShowActions = alwaysDisplayActions || (isHovered && showActions);

  return (
    <div
      className={cn('relative group', className)}
      onMouseEnter={actions ? handleMouseEnter : undefined}
      onMouseLeave={actions ? handleMouseLeave : undefined}
    >
      {children}

      {actions && (
        <div
          data-slot="hover-actions"
          onClick={handleActionsClick}
          className={cn(
            'transition-all absolute z-10',
            positionClasses[actionsPosition],
            'opacity-0 pointer-events-none',
            {
              'opacity-100 pointer-events-auto': shouldShowActions,
              'focus-within:pointer-events-auto focus-within:opacity-100': isHovered,
            },
            actionsClassName,
          )}
        >
          {actions}
        </div>
      )}
    </div>
  );
};

export const HoverActions = memo(HoverActionsBase);
